import { clientWithApiClient } from "../../client";
import { appendRegionIds } from "../regions/params";
import { appendConsignedOnly } from "../consigned-owners/params";
import type { ApiClient } from "../../types";

import type { ListResponse, Response } from "../../types";
import type {
  ServiceStatus,
  Vehicle,
  CreateVehicleRequest,
  UpdateVehicleRequest,
  VehicleFilters,
  VehicleListOptions,
  VehicleListResponse,
  BulkVehicleLocationRequest,
  BulkVehicleLocationResponse,
} from "./types";
import type { ConsignedOwner } from "../consigned-owners/types";

/**
 * Builds the query params shared by `list` and `listPaginated`.
 *
 * Kept as one function so a new filter is added once and both calls send
 * it, instead of the two drifting apart the way `list` alone used to.
 */
function buildVehicleSearchParams(
  fleetId: string,
  filters?: VehicleFilters,
): URLSearchParams {
  const searchParams = new URLSearchParams();

  searchParams.append("fleet_id", fleetId);

  // Region scope. Emits nothing for an absent or empty selection, so a fleet
  // without regions sends exactly the request it sends today.
  appendRegionIds(searchParams, filters?.region_ids);

  // Vehicle reach, on the same rule: nothing is emitted unless it was asked
  // for, and the server resolves "mine" from the caller's own token.
  appendConsignedOnly(searchParams, filters?.consigned_only);

  // Basic filters
  if (filters?.name) searchParams.append("name", filters.name);
  if (filters?.search) searchParams.append("search", filters.search);
  if (filters?.description)
    searchParams.append("description", filters.description);
  if (filters?.status) searchParams.append("status", filters.status);
  if (filters?.created_after)
    searchParams.append("created_after", filters.created_after);
  if (filters?.created_before)
    searchParams.append("created_before", filters.created_before);

  // Vehicle detail filters
  if (filters?.year) searchParams.append("year", filters.year.toString());
  if (filters?.make) searchParams.append("make", filters.make);
  if (filters?.model) searchParams.append("model", filters.model);
  if (filters?.category) searchParams.append("category", filters.category);
  if (filters?.vehicle_type_id !== undefined)
    searchParams.append("vehicle_type_id", filters.vehicle_type_id.toString());

  // Boolean filters
  if (filters?.active !== undefined)
    searchParams.append("active", filters.active.toString());
  if (filters?.featured !== undefined)
    searchParams.append("featured", filters.featured.toString());
  if (filters?.instant_book !== undefined)
    searchParams.append("instant_book", filters.instant_book.toString());
  if (filters?.pets_allowed !== undefined)
    searchParams.append("pets_allowed", filters.pets_allowed.toString());
  if (filters?.smoking_allowed !== undefined)
    searchParams.append("smoking_allowed", filters.smoking_allowed.toString());
  if (filters?.delivery_available !== undefined)
    searchParams.append(
      "delivery_available",
      filters.delivery_available.toString(),
    );
  if (filters?.delivery_custom_address !== undefined)
    searchParams.append(
      "delivery_custom_address",
      filters.delivery_custom_address.toString(),
    );

  return searchParams;
}

/**
 * Retrieves the full list of vehicles from a specific fleet
 *
 * The fleetId is crucial as it acts as a resource filter - vehicles are scoped to their fleet,
 * ensuring users can only access vehicles that belong to their authorized fleet.
 *
 * Never sends `page` / `page_size`: every existing caller keeps getting the
 * whole list. Use `listPaginated` for the Vehicles page.
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID that owns the vehicles. This is required for resource filtering
 * @param filters - Optional filters to apply to the vehicle list
 * @returns Promise that resolves to the full list of vehicles
 *
 * @example
 * ```typescript
 * const { data: vehicles } = await vehicles.list(apiClient, "fleet-123", {
 *   status: "active",
 *   make: "Toyota",
 *   year: 2023,
 *   active: true,
 *   delivery_available: true
 * });
 * ```
 */
const list = (
  apiClient: ApiClient,
  fleetId: string,
  filters?: VehicleFilters,
) => {
  const searchParams = buildVehicleSearchParams(fleetId, filters);

  const queryString = searchParams.toString();
  const url = queryString ? `/vehicles?${queryString}` : "/vehicles";

  return clientWithApiClient<ListResponse<Vehicle>>(apiClient, url);
};

/**
 * Retrieves one page of vehicles from a specific fleet, with a total count.
 *
 * Used only by the Vehicles page. Unlike `list`, it always sends `page` and
 * `page_size`, which opts the request into the server's pagination branch.
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID that owns the vehicles
 * @param filters - Optional filters to apply to the vehicle list
 * @param pagination - The page and page size to request
 * @param options - Opt-in extras (status counts, card stats, status and class
 * filters). Each is sent only when set, so omitting them sends exactly the
 * request this call has always sent.
 * @returns Promise that resolves to one page of vehicles plus `meta`
 *
 * @example
 * ```typescript
 * const { data, meta } = await vehicles.listPaginated(apiClient, "fleet-123", {
 *   search: "sprinter",
 * }, { page: 2, pageSize: 25 });
 * ```
 */
const listPaginated = (
  apiClient: ApiClient,
  fleetId: string,
  filters: VehicleFilters | undefined,
  pagination: { page: number; pageSize: number },
  options?: VehicleListOptions,
) => {
  const searchParams = buildVehicleSearchParams(fleetId, filters);

  searchParams.append("page", pagination.page.toString());
  searchParams.append("page_size", pagination.pageSize.toString());

  if (options?.includeStatusCounts) {
    searchParams.append("include_status_counts", "true");
  }
  if (options?.includeCardStats) {
    searchParams.append("include_card_stats", "true");
  }
  if (options?.displayStatus) {
    searchParams.append("display_status", options.displayStatus);
  }
  if (options?.rvClassId !== undefined) {
    searchParams.append("rv_class_id", options.rvClassId.toString());
  }

  const url = `/vehicles?${searchParams.toString()}`;

  return clientWithApiClient<VehicleListResponse>(apiClient, url);
};

/**
 * Retrieves a specific vehicle by its ID within a fleet
 *
 * The fleetId ensures that users can only access vehicles that belong to their authorized fleet,
 * providing security and data isolation between different fleets.
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID that owns the vehicle. Required for resource access control
 * @param id - Unique identifier of the vehicle
 * @returns Promise that resolves to the requested vehicle
 *
 * @throws {Error} If the vehicle doesn't exist or doesn't belong to the specified fleet
 *
 * @example
 * ```typescript
 * const { data: vehicle } = await vehicles.get(apiClient, "fleet-123", "vehicle-456");
 * ```
 */
const get = (apiClient: ApiClient, fleetId: string, id: string) => {
  const searchParams = new URLSearchParams();

  searchParams.append("fleet_id", fleetId);

  const queryString = searchParams.toString();
  const url = queryString
    ? `/vehicles/${id}?${queryString}`
    : `/vehicles/${id}`;

  return clientWithApiClient<Response<Vehicle>>(apiClient, url);
};

/**
 * Creates a new vehicle in the specified fleet
 *
 * The fleetId determines which fleet the vehicle will belong to. This is essential for
 * resource organization and access control, as all vehicles must be associated with a fleet.
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID where the vehicle will be created. Required for resource association
 * @param vehicle - Vehicle data to create (without the ID)
 * @returns Promise that resolves to the created vehicle with its assigned ID
 *
 * @throws {Error} If the vehicle data is invalid or the fleet doesn't exist
 *
 * @example
 * ```typescript
 * const { data: newVehicle } = await vehicles.create(apiClient, "fleet-123", {
 *   name: "Toyota Camry 2023",
 *   status: "active",
 *   description: "Executive sedan",
 *   year: 2023,
 *   make: "Toyota",
 *   model: "Camry",
 *   category: "sedan",
 *   sleeps: 5,
 *   delivery_available: true,
 *   pets_allowed: false,
 *   active: true
 * });
 * ```
 */
const create = (
  apiClient: ApiClient,
  fleetId: string,
  vehicle: CreateVehicleRequest,
) => {
  const searchParams = new URLSearchParams();

  searchParams.append("fleet_id", fleetId);

  const queryString = searchParams.toString();
  const url = queryString ? `/vehicles?${queryString}` : "/vehicles";

  return clientWithApiClient<Response<Vehicle>>(apiClient, url, {
    method: "POST",
    body: JSON.stringify({ vehicle: vehicle }),
  });
};

/**
 * Updates an existing vehicle
 *
 * The optional fleetId parameter provides additional validation to ensure the vehicle
 * belongs to the expected fleet, maintaining data integrity and access control.
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID that owns the vehicle. Required for resource access control
 * @param id - Unique identifier of the vehicle
 * @param vehicle - Updated vehicle data (without the ID)
 * @returns Promise that resolves to the updated vehicle
 *
 * @throws {Error} If the vehicle doesn't exist or the data is invalid
 *
 * @example
 * ```typescript
 * const { data: updatedVehicle } = await vehicles.update(apiClient, "vehicle-456", {
 *   name: "Toyota Camry 2023 - Updated",
 *   status: "maintenance",
 *   description: "Scheduled maintenance"
 * }, "fleet-123");
 * ```
 */
const update = (
  apiClient: ApiClient,
  fleetId: string,
  id: string,
  vehicle: UpdateVehicleRequest,
) => {
  const searchParams = new URLSearchParams();

  searchParams.append("fleet_id", fleetId);

  const queryString = searchParams.toString();
  const url = queryString
    ? `/vehicles/${id}?${queryString}`
    : `/vehicles/${id}`;

  return clientWithApiClient<Response<Vehicle>>(apiClient, url, {
    method: "PATCH",
    body: JSON.stringify({ vehicle: vehicle }),
  });
};

/**
 * Deletes a vehicle from the fleet
 *
 * The optional fleetId parameter ensures that the deletion operation is performed
 * within the correct fleet context, preventing unauthorized deletions across fleets.
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID that owns the vehicle. Required for resource access control
 * @param id - Unique identifier of the vehicle
 * @returns Promise that resolves when the vehicle has been successfully deleted
 *
 * @throws {Error} If the vehicle doesn't exist or cannot be deleted
 *
 * @example
 * ```typescript
 * await vehicles.delete(apiClient, "fleet-123", "vehicle-456");
 * ```
 */
const remove = (apiClient: ApiClient, fleetId: string, id: string) => {
  const searchParams = new URLSearchParams();

  searchParams.append("fleet_id", fleetId);

  const queryString = searchParams.toString();
  const url = queryString
    ? `/vehicles/${id}?${queryString}`
    : `/vehicles/${id}`;

  return clientWithApiClient(apiClient, url, {
    method: "DELETE",
  });
};

/**
 * Retrieves the consigned owner for a specific vehicle
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet ID that owns the vehicle
 * @param vehicleId - Unique identifier (public_id UUID) of the vehicle
 * @returns Promise that resolves to the consigned owner or null if no owner assigned
 *
 * @example
 * ```typescript
 * const { data: owner } = await vehicles.getConsignedOwner(apiClient, "fleet-123", "vehicle-456");
 * ```
 */
const getServiceStatus = (
  apiClient: ApiClient,
  fleetId: string,
  vehicleId: string,
) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);

  return clientWithApiClient<Response<ServiceStatus>>(
    apiClient,
    `/vehicles/${vehicleId}/service-status?${searchParams.toString()}`,
  );
};

const getConsignedOwner = (
  apiClient: ApiClient,
  fleetId: string,
  vehicleId: string,
) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);

  const queryString = searchParams.toString();
  const url = queryString
    ? `/vehicles/${vehicleId}/consigned-owner?${queryString}`
    : `/vehicles/${vehicleId}/consigned-owner`;

  return clientWithApiClient<Response<ConsignedOwner>>(apiClient, url);
};

/**
 * Writes one pickup address and/or one set of delivery rules onto many
 * vehicles at once.
 *
 * All-or-nothing server side: a fleet fixing forty addresses needs to know
 * it either took or did not, rather than hunting for which half landed.
 *
 * @param apiClient - Configured API client instance
 * @param fleetId - The fleet that owns every vehicle in the request
 * @param request - The vehicle ids plus the halves being edited
 * @returns How many vehicles were written, and any the server refused
 *
 * @example
 * ```typescript
 * const { data } = await vehicles.bulkLocation(apiClient, "fleet-123", {
 *   vehicle_ids: ["v1", "v2"],
 *   pickup: { pickup_address: "1 Main St", pickup_place_id: "place-1" },
 * });
 * ```
 */
const bulkLocation = (
  apiClient: ApiClient,
  fleetId: string,
  request: BulkVehicleLocationRequest,
) => {
  const searchParams = new URLSearchParams();

  searchParams.append("fleet_id", fleetId);

  const queryString = searchParams.toString();
  const url = queryString
    ? `/vehicles/bulk-location?${queryString}`
    : "/vehicles/bulk-location";

  return clientWithApiClient<BulkVehicleLocationResponse>(apiClient, url, {
    method: "POST",
    // Not wrapped in `{ vehicle: ... }` like the single-vehicle PATCH: this
    // body is about a set of vehicles, not about one.
    body: JSON.stringify(request),
  });
};

/**
 * Vehicles API for fleet management
 *
 * Provides complete CRUD operations for vehicle management within fleets.
 * All operations are scoped by fleetId to ensure proper resource isolation and access control.
 *
 */
export const vehicles = {
  list,
  listPaginated,
  get,
  create,
  update,
  delete: remove,
  getConsignedOwner,
  getServiceStatus,
  bulkLocation,
};
